import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.js';

/**
 * Renders the Workfront / Fusion / Custom-Fields metadata tables
 * inside the scenario detail modal. Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenarioModal/{WorkfrontIdTable,
 * fusionMetadata/*, customFieldsTable}.tsx` set, collapsed into one
 * file because every section is structurally the same — a typed
 * header, a defensive parser over the engine's `z.unknown()` fields,
 * and a small table.
 *
 * Gated on `view !== 'business'`: business executives don't see the
 * developer-leaning metadata. Admins + developers see everything that
 * has data.
 */

export interface ScenarioMetadataInput {
  /** Typed array — engine's `ScenarioDetailWorkfrontObjectSchema`. */
  workfrontObjects?: ReadonlyArray<{
    id: number;
    workfrontId: string;
    workfrontType: string | null;
    workfrontTypeLabel: string | null;
    workfrontLink: string | null;
    workfrontName: string | null;
    fusionModuleId: string;
    fusionModuleName: string;
    fusionModuleType: string;
  }>;
  /** Engine `z.unknown()` — defensively parsed at render time.
   *  Likely shape: `[{ id, name }]`. */
  connectionData?: unknown;
  dataStoreData?: unknown;
  dataStructureData?: unknown;
  /** Likely shape: `[{ name, value }]`. */
  variables?: unknown;
  /** Free-text URL list — newline-separated. */
  webhooks?: string | null;
  /** Likely shape: `[{ field, name, modules: [{moduleId, moduleName}] }]`. */
  customFields?: unknown;
  /** Planning objects (Maestro `Ws…`/`Rt…`/`F…`/`Rc…` references)
   *  detected on this scenario's blueprints. Sourced from the
   *  license-wide `/v1/scenarios/planning-objects-data` fetch and
   *  pivoted by `fusionScenarioId` upstream, so this is already
   *  filtered to the open scenario. */
  planningObjects?: ReadonlyArray<{
    planningObjectType: string;
    planningObjectId: string;
    objectName: string | null;
    recordTypeName: string | null;
    fusionModuleId: string | null;
    fusionModuleName: string | null;
  }>;
}

// Same friendly-label map as `ScenarioExpandPanel.tsx` for the
// per-Maestro-record-kind sub-headers. Keep these two in sync if a new
// `planningObjectType` value ever shows up.
const PLANNING_TYPE_LABEL: Record<string, string> = {
  Ws: 'Workspace',
  Rt: 'Record Type',
  F: 'Field',
  Rc: 'Record',
};
const PLANNING_TYPE_ORDER = ['Ws', 'Rt', 'F', 'Rc'];

interface ScenarioMetadataSectionProps {
  data: ScenarioMetadataInput;
  /** Fusion scenario `url` — used to deep-link rows to the Fusion
   *  blueprint editor (mirrors legacy WorkfrontIdTable affordance). */
  scenarioUrl?: string | null;
  /** Operator's currently-selected view. Business hides this section
   *  entirely; admin + developer render. */
  view: 'business' | 'administrator' | 'developer';
}

export function ScenarioMetadataSection({
  data,
  scenarioUrl,
  view,
}: ScenarioMetadataSectionProps): JSX.Element | null {
  if (view === 'business') return null;

  const wfObjects = data.workfrontObjects ?? [];
  const connections = parseNamedList(data.connectionData);
  const dataStores = parseNamedList(data.dataStoreData);
  const dataStructures = parseNamedList(data.dataStructureData);
  const variables = parseKeyValueList(data.variables);
  const customFields = parseCustomFields(data.customFields);
  const webhooks = parseWebhooks(data.webhooks);
  const planningObjects = data.planningObjects ?? [];
  type PlanningRow = (typeof planningObjects)[number];
  // Group planning objects by Maestro record-kind for the sub-headers.
  const planningGroups = (() => {
    const map = new Map<string, PlanningRow[]>();
    for (const p of planningObjects) {
      const arr = map.get(p.planningObjectType) ?? [];
      arr.push(p);
      map.set(p.planningObjectType, arr);
    }
    return [...map.entries()]
      .map(([type, items]) => ({ type, items }))
      .sort((a, b) => {
        const ai = PLANNING_TYPE_ORDER.indexOf(a.type);
        const bi = PLANNING_TYPE_ORDER.indexOf(b.type);
        const aiSort = ai === -1 ? PLANNING_TYPE_ORDER.length : ai;
        const biSort = bi === -1 ? PLANNING_TYPE_ORDER.length : bi;
        if (aiSort !== biSort) return aiSort - biSort;
        return a.type.localeCompare(b.type);
      });
  })();

  const hasAny =
    wfObjects.length > 0 ||
    connections.length > 0 ||
    dataStores.length > 0 ||
    dataStructures.length > 0 ||
    variables.length > 0 ||
    customFields.length > 0 ||
    planningObjects.length > 0 ||
    webhooks.length > 0;

  if (!hasAny) return null;

  return (
    <section className="rounded-md border border-foreground/10">
      <CollapsibleHeader label="Workfront & Fusion Metadata" />
      <div className="space-y-6 p-4">
        {wfObjects.length > 0 ? (
          <Subsection title="Workfront Objects">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="border-foreground/10">
                  <TableHead className="text-foreground/60">Workfront type</TableHead>
                  <TableHead className="text-foreground/60">Workfront name</TableHead>
                  <TableHead className="text-foreground/60">Fusion module</TableHead>
                  <TableHead className="text-foreground/60">Workfront ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wfObjects.map((o) => (
                  <TableRow key={o.id} className="border-foreground/10">
                    <TableCell className="text-foreground/80">
                      {o.workfrontTypeLabel ?? o.workfrontType ?? '—'}
                    </TableCell>
                    <TableCell className="text-foreground/80">{o.workfrontName ?? '—'}</TableCell>
                    <TableCell className="text-foreground/80">
                      <span className="block">{o.fusionModuleName}</span>
                      <span className="block text-[10px] text-foreground/40">
                        {o.fusionModuleType}
                      </span>
                    </TableCell>
                    <TableCell className="text-end text-foreground/80">
                      {o.workfrontLink ? (
                        <a
                          href={o.workfrontLink}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center justify-end gap-1 text-foreground/70 hover:text-foreground"
                        >
                          <span className="underline">{o.workfrontId}</span>
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        o.workfrontId
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Subsection>
        ) : null}

        {customFields.length > 0 ? (
          <Subsection title="Custom Fields">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="border-foreground/10">
                  <TableHead className="text-foreground/60">Field</TableHead>
                  <TableHead className="text-foreground/60">Name</TableHead>
                  <TableHead className="text-foreground/60">Module</TableHead>
                  <TableHead className="text-end text-foreground/60">Module ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customFields.map((cf, i) => {
                  const mod = cf.modules[0];
                  return (
                    <TableRow key={`${cf.field}-${i}`} className="border-foreground/10">
                      <TableCell className="text-foreground/80">{cf.field}</TableCell>
                      <TableCell className="text-foreground/80">{cf.name}</TableCell>
                      <TableCell className="text-foreground/80">{mod?.moduleName ?? '—'}</TableCell>
                      <TableCell className="text-end text-foreground/80">
                        {mod && scenarioUrl ? (
                          <a
                            href={`${scenarioUrl}/edit?moduleId=${encodeURIComponent(mod.moduleId)}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center justify-end gap-1 text-foreground/70 hover:text-foreground"
                          >
                            <span className="underline">{mod.moduleId}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          (mod?.moduleId ?? '—')
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Subsection>
        ) : null}

        {planningObjects.length > 0 ? (
          <Subsection title={`Planning Objects (${planningObjects.length})`}>
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="border-foreground/10">
                  <TableHead className="text-foreground/60">Planning ID</TableHead>
                  <TableHead className="text-foreground/60">Name</TableHead>
                  <TableHead className="text-foreground/60">Module</TableHead>
                  <TableHead className="text-end text-foreground/60">Module #</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {planningGroups.flatMap((group) => [
                  <TableRow
                    key={`pl-header-${group.type}`}
                    className="border-foreground/10 bg-foreground/[0.03]"
                  >
                    <TableCell
                      colSpan={4}
                      className="text-[10px] uppercase tracking-wider text-foreground/50"
                    >
                      {PLANNING_TYPE_LABEL[group.type] ?? group.type} ({group.items.length})
                    </TableCell>
                  </TableRow>,
                  ...group.items.map((p, i) => (
                    <TableRow
                      key={`pl-${group.type}-${i}-${p.planningObjectId}-${p.fusionModuleId ?? 'nomod'}`}
                      className="border-foreground/10"
                    >
                      <TableCell className="font-mono text-foreground/80">
                        {p.planningObjectId}
                      </TableCell>
                      <TableCell className="text-foreground/80">
                        {p.objectName ?? p.recordTypeName ?? '—'}
                      </TableCell>
                      <TableCell className="text-foreground/80">
                        {p.fusionModuleName || '—'}
                      </TableCell>
                      <TableCell className="text-end text-foreground/80">
                        {p.fusionModuleId && scenarioUrl ? (
                          <a
                            href={`${scenarioUrl}/edit?moduleId=${encodeURIComponent(p.fusionModuleId)}`}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center justify-end gap-1 text-foreground/70 hover:text-foreground"
                          >
                            <span className="underline">{p.fusionModuleId}</span>
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          (p.fusionModuleId ?? '—')
                        )}
                      </TableCell>
                    </TableRow>
                  )),
                ])}
              </TableBody>
            </Table>
          </Subsection>
        ) : null}

        {/* Developer-only blocks: Connections, Data Stores, Data Structures,
            Variables, Webhooks. Admins land them as well in legacy — the
            payoff vs hiding is marginal and the legacy is the parity bar. */}
        {connections.length > 0 ? (
          <NamedListTable title="Fusion Connections" rows={connections} />
        ) : null}
        {dataStores.length > 0 ? (
          <NamedListTable title="Fusion Data Stores" rows={dataStores} />
        ) : null}
        {dataStructures.length > 0 ? (
          <NamedListTable title="Fusion Data Structures" rows={dataStructures} />
        ) : null}
        {variables.length > 0 ? (
          <Subsection title="Fusion Variables">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="border-foreground/10">
                  <TableHead className="text-foreground/60">Name</TableHead>
                  <TableHead className="text-foreground/60">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {variables.map((v, i) => (
                  <TableRow key={`${v.name}-${i}`} className="border-foreground/10">
                    <TableCell className="break-words text-foreground/80">{v.name}</TableCell>
                    <TableCell className="break-all font-mono text-foreground/70">
                      {v.value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Subsection>
        ) : null}
        {webhooks.length > 0 ? (
          <Subsection title="Fusion Webhooks">
            <ul className="space-y-1 text-xs">
              {webhooks.map((url, i) => (
                <li key={i} className="break-all font-mono text-foreground/70">
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="hover:text-foreground"
                  >
                    {url}
                  </a>
                </li>
              ))}
            </ul>
          </Subsection>
        ) : null}
      </div>
    </section>
  );
}

function CollapsibleHeader({ label }: { label: string }): JSX.Element {
  // Section-level collapsible affordance lives on the parent. This
  // header just sits at the top of the section to label it.
  return (
    <div className="flex w-full items-center gap-2 border-b border-foreground/10 px-3 py-2 text-sm font-medium uppercase tracking-wider text-foreground/80">
      {label}
    </div>
  );
}

function Subsection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): JSX.Element {
  const [open, setOpen] = useState(true);
  return (
    <article>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="mb-2 flex w-full items-center gap-2 text-xs font-semibold uppercase tracking-wide text-foreground/70 hover:text-foreground"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {title}
      </button>
      {open ? children : null}
    </article>
  );
}

function NamedListTable({
  title,
  rows,
}: {
  title: string;
  rows: ReadonlyArray<{ id: string; name: string }>;
}): JSX.Element {
  return (
    <Subsection title={title}>
      <Table className="text-xs">
        <TableHeader>
          <TableRow className="border-foreground/10">
            <TableHead className="text-foreground/60">Name</TableHead>
            <TableHead className="text-end text-foreground/60">ID</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="border-foreground/10">
              <TableCell className="text-foreground/80">{r.name}</TableCell>
              <TableCell className="text-end font-mono text-foreground/70">{r.id}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Subsection>
  );
}

/* ── Defensive parsers ──
 *
 * The engine returns these blobs as `z.unknown()` because the legacy
 * Fusion payloads carry shapes that we don't validate at the boundary.
 * We narrow at render time and silently drop anything that doesn't
 * match — better an incomplete table than a runtime crash on a
 * surprise shape. Matches the legacy SPA's approach (its `IScenarioData`
 * was equally lax internally — the type annotations were aspirational).
 */

function parseNamedList(value: unknown): ReadonlyArray<{ id: string; name: string }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const id = typeof e.id === 'string' || typeof e.id === 'number' ? String(e.id) : null;
    const name = typeof e.name === 'string' ? e.name : null;
    if (id === null || name === null) return [];
    return [{ id, name }];
  });
}

function parseKeyValueList(value: unknown): ReadonlyArray<{ name: string; value: string }> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const name = typeof e.name === 'string' ? e.name : null;
    if (name === null) return [];
    const raw = e.value;
    const formatted =
      typeof raw === 'string' ? raw : raw === null || raw === undefined ? '' : JSON.stringify(raw);
    return [{ name, value: formatted }];
  });
}

interface ParsedCustomField {
  field: string;
  name: string;
  modules: ReadonlyArray<{ moduleId: string; moduleName: string }>;
}

function parseCustomFields(value: unknown): ReadonlyArray<ParsedCustomField> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const e = entry as Record<string, unknown>;
    const field = typeof e.field === 'string' ? e.field : null;
    const name = typeof e.name === 'string' ? e.name : null;
    if (field === null || name === null) return [];
    const modulesRaw = Array.isArray(e.modules) ? e.modules : [];
    const modules = modulesRaw.flatMap((m) => {
      if (!m || typeof m !== 'object') return [];
      const mm = m as Record<string, unknown>;
      const moduleId =
        typeof mm.id === 'string' || typeof mm.id === 'number' ? String(mm.id) : null;
      const moduleName = typeof mm.moduleName === 'string' ? mm.moduleName : null;
      if (moduleId === null || moduleName === null) return [];
      return [{ moduleId, moduleName }];
    });
    return [{ field, name, modules }];
  });
}

/**
 * Webhooks come back as either a newline-separated URL list or a
 * single URL. Split + trim + filter so an empty trailing newline
 * doesn't produce a blank row.
 */
function parseWebhooks(value: string | null | undefined): ReadonlyArray<string> {
  if (!value) return [];
  return value
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}
